/**
 * The send ledger (FEAT-038 wave 2) — the durable list of what is leaving this
 * phone, and the only place a send's destination is written down.
 *
 * It lives in the `queue` store of `momento-media`, beside the bytes it points
 * at, so a note about a send and the media that send needs can never end up in
 * two databases that disagree about which one survived a crash.
 *
 * Two rules this file exists to keep:
 *
 * - **Labels only.** `QueueRow` has `bytes?: never`. Every read here is a read
 *   of small records; the media is fetched one chunk at a time by the engine,
 *   at the one moment its upload is moving.
 * - **Disk first.** Every mutation is written to IndexedDB and only then
 *   mirrored in memory. The mirror is what makes `activeMediaIds()` a
 *   synchronous answer for the drafts sheet; it is never the source of truth.
 *
 * It FAILS LOUD. The retired session store resolved null on any IDB error,
 * which made "this send is not durable" look exactly like "fine" — a queue
 * that silently isn't durable is worse than one that refuses.
 */
import {
  KV_STORE,
  QUEUE_STORE,
  SESSION_STORE,
  idbRequest,
  mediaStore,
} from '../media/mediaStore';
import { evacuation } from '../media/evacuate';
import type { QueueRow, SessionRow } from './types';

class Ledger {
  /** Mirror of the `queue` store. Written only after the disk write lands. */
  private rows = new Map<string, QueueRow>();
  /**
   * Mirror of the `sessions` store (wave 3). An upload's `committedOffset` is
   * the offset the SERVER confirmed, so mirroring it makes the progress a
   * surface can read synchronously — and honest by construction. Nothing here
   * invents a percentage (BUG-058's rule).
   */
  private uploads = new Map<string, SessionRow>();
  private ready: Promise<void> | null = null;
  private changeCallbacks = new Set<() => void>();
  private uploadCallbacks = new Set<() => void>();

  /**
   * Load the ledger. Metadata only, zero network, zero media bytes — this runs
   * on the boot path and the whole campaign exists because things that ran on
   * the boot path read videos.
   */
  init(): Promise<void> {
    if (!this.ready) {
      this.ready = evacuation
        .done()
        .then(() => this.load())
        .catch((err) => {
          this.ready = null; // a failed open must not poison every later call
          throw err;
        });
    }
    return this.ready;
  }

  private async load(): Promise<void> {
    // A cursor, not getAll: nothing in either store is big, and keeping the
    // habit is cheaper than re-learning why it matters.
    const rows = await this.walk<QueueRow>(QUEUE_STORE);
    this.rows = new Map(rows.map((row) => [row.id, row]));
    const sessions = await this.walk<SessionRow>(SESSION_STORE);
    this.uploads = new Map(sessions.map((row) => [row.mediaId, row]));
    // The mirror just went from empty to real. Every subscriber that computed
    // against the empty pre-boot ledger — a drafts count excluding
    // activeMediaIds(), the queue sheet's rows — is wrong RIGHT NOW and, with
    // nothing else mutating (offline, drain declines to run), would stay wrong
    // all session. This is the self-correction BUG-122 was missing.
    this.fireChange();
    this.fireUploadChange();
  }

  private walk<T>(store: typeof QUEUE_STORE | typeof SESSION_STORE): Promise<T[]> {
    return mediaStore.withStore(store, 'readonly', (objectStore) => {
      return new Promise<T[]>((resolve, reject) => {
        const out: T[] = [];
        const request = objectStore.openCursor();
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) {
            resolve(out);
            return;
          }
          out.push(cursor.value as T);
          cursor.continue();
        };
        request.onerror = () => reject(request.error);
      });
    });
  }

  // ---- reading (synchronous: the mirror is already in memory) ----

  all(): QueueRow[] {
    return [...this.rows.values()];
  }

  get(id: string): QueueRow | undefined {
    return this.rows.get(id);
  }

  byMedia(mediaId: string): QueueRow[] {
    return this.all().filter((row) => row.mediaId === mediaId);
  }

  /** Media the queue is still responsible for — what draft surfaces exclude so
   * a moment being delivered right now never surfaces as "unsent". A STUCK row
   * is deliberately not live: that moment needs a decision, so it must be
   * visible again. */
  activeMediaIds(): Set<string> {
    const ids = new Set<string>();
    for (const row of this.rows.values()) {
      if (row.state !== 'stuck') ids.add(row.draftId ?? row.mediaId);
    }
    return ids;
  }

  // ---- writing (disk first, then the mirror) ----

  /** Commit rows in ONE transaction. Enqueue uses it so a moment fanned out to
   * four places can never be half-committed: either every destination is on
   * disk or none is. */
  async putMany(rows: QueueRow[]): Promise<void> {
    if (rows.length === 0) return;
    await mediaStore.withStore(QUEUE_STORE, 'readwrite', (store) =>
      Promise.all(rows.map((row) => idbRequest(store.put(row))))
    );
    for (const row of rows) this.rows.set(row.id, row);
    this.fireChange();
  }

  put(row: QueueRow): Promise<void> {
    return this.putMany([row]);
  }

  /**
   * Change one row, on disk first. Returns the new row, or null if it was
   * removed in the meantime (a discard racing a pass).
   */
  async patch(id: string, updates: Partial<QueueRow>): Promise<QueueRow | null> {
    const current = this.rows.get(id);
    if (!current) return null;
    const next: QueueRow = { ...current, ...updates, updatedAt: Date.now() };
    await mediaStore.withStore(QUEUE_STORE, 'readwrite', (store) =>
      idbRequest(store.put(next))
    );
    this.rows.set(id, next);
    this.fireChange();
    return next;
  }

  async remove(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    await mediaStore.withStore(QUEUE_STORE, 'readwrite', (store) =>
      Promise.all(ids.map((id) => idbRequest(store.delete(id))))
    );
    for (const id of ids) this.rows.delete(id);
    this.fireChange();
  }

  // ---- upload sessions (one per media, not per row) ----

  async session(mediaId: string): Promise<SessionRow | null> {
    const row = await mediaStore.withStore(SESSION_STORE, 'readonly', (store) =>
      idbRequest(store.get(mediaId))
    );
    return (row as SessionRow | undefined) ?? null;
  }

  async putSession(session: SessionRow): Promise<void> {
    const next = { ...session, updatedAt: Date.now() };
    await mediaStore.withStore(SESSION_STORE, 'readwrite', (store) =>
      idbRequest(store.put(next))
    );
    this.uploads.set(next.mediaId, next);
    this.fireUploadChange();
  }

  async removeSession(mediaId: string): Promise<void> {
    await mediaStore.withStore(SESSION_STORE, 'readwrite', (store) =>
      idbRequest(store.delete(mediaId))
    );
    this.uploads.delete(mediaId);
    this.fireUploadChange();
  }

  /** Every upload the phone is part-way through, from the mirror. */
  sessions(): SessionRow[] {
    return [...this.uploads.values()];
  }

  /**
   * Sessions no queue row owns. Two kinds live here, and the engine tells them
   * apart by the media: a HEAD START (its draft is still on the phone, so the
   * session is that draft's progress) and the leavings of a send that finished
   * or was discarded (its media is gone, so the session is litter).
   */
  orphanSessionIds(): string[] {
    const owned = new Set(this.all().map((row) => row.mediaId));
    return this.sessions()
      .map((session) => session.mediaId)
      .filter((mediaId) => !owned.has(mediaId));
  }

  // ---- small durable facts, shared with the migration ----

  kvGet<T>(key: string): Promise<T | null> {
    return mediaStore.withStore(KV_STORE, 'readonly', async (store) => {
      const row = await idbRequest(store.get(key));
      return (row as T | undefined) ?? null;
    });
  }

  async kvSet(key: string, value: Record<string, unknown>): Promise<void> {
    await mediaStore.withStore(KV_STORE, 'readwrite', (store) =>
      idbRequest(store.put({ key, ...value }))
    );
  }

  // ---- change signal ----

  /** Fires immediately on subscribe — surfaces render current state first. */
  onChange(cb: () => void): () => void {
    this.changeCallbacks.add(cb);
    cb();
    return () => this.changeCallbacks.delete(cb);
  }

  /**
   * Upload progress only — deliberately NOT `onChange`.
   *
   * A committed offset lands every ~2 MiB, and `onChange`'s subscribers
   * recount the drafts sheet, which walks the whole meta store. Firing one
   * signal for both would mean a 400 MB clip triggering two hundred full
   * metadata walks: the exact shape of the bug this campaign is about.
   */
  onUploadChange(cb: () => void): () => void {
    this.uploadCallbacks.add(cb);
    cb();
    return () => this.uploadCallbacks.delete(cb);
  }

  private fireChange(): void {
    for (const cb of this.changeCallbacks) cb();
  }

  private fireUploadChange(): void {
    for (const cb of this.uploadCallbacks) cb();
  }

  /** Tests only. */
  _resetForTests(): void {
    this.rows.clear();
    this.uploads.clear();
    this.ready = null;
    this.changeCallbacks.clear();
    this.uploadCallbacks.clear();
  }
}

export const ledger = new Ledger();
